import { html, css, nothing, type TemplateResult } from 'lit';

// The shared field anatomy — label / required marker / help / error — for
// every cos-* form-control primitive. Extracted verbatim from cos-input.ts
// (the reference implementation): `fieldAnatomyStyles` is a byte-identical
// copy of its `.label-text`/`.req`/`.msg` rules, and `renderFieldLabel` /
// `renderFieldMessage` are its label-row and message-row templates pulled
// out unchanged. cos-input, cos-textarea and cos-date-picker consume this
// module directly with no visual change; primitives that already carry a
// DIFFERENT but visually-equivalent local anatomy (cos-slider) adopt it
// too. A primitive with its own distinct, already-shipped label idiom
// (cos-checkbox's inline label, cos-toggle's inline label) keeps its own
// classes and reuses only the id-generation + message-resolution logic —
// forcing this module's class names onto an already-different look would
// silently reflow it.
//
// A `css` constant rather than a shared stylesheet — each primitive has
// its own shadow root, so a document-level rule wouldn't reach it (the
// `design-system/source-chip.ts` precedent).

let _nextMsgId = 1;

/**
 * A unique DOM id for a field's help/error message, namespaced by the
 * calling primitive's own tag (e.g. `cos-input`) so ids stay legible in
 * devtools and never collide across different primitive types on one page.
 */
export function nextFieldMessageId(prefix: string): string {
	return `${prefix}-msg-${_nextMsgId++}`;
}

export interface FieldLabelOptions {
	label: string;
	required?: boolean;
	hideLabel?: boolean;
	/** Set when a caller needs to point `aria-labelledby` at this span
	 * (a composite control with no single labelable descendant to wrap in
	 * a `<label>` — cos-select's panel, cos-multiselect's group). */
	id?: string;
}

/**
 * The visible (or visually-hidden-but-in-the-a11y-tree) caption + required
 * asterisk that heads a field. Renders `nothing` when there is no label
 * text — so a primitive that hasn't opted a given consumer into a label
 * renders exactly as it did before this module existed.
 */
export function renderFieldLabel(opts: FieldLabelOptions): TemplateResult | typeof nothing {
	if (!opts.label) return nothing;
	return html`<span
		id=${opts.id ?? nothing}
		class=${opts.hideLabel ? 'label-text visually-hidden' : 'label-text'}
		>${opts.label}${opts.required
			? html`<span class="req" aria-hidden="true"> *</span>`
			: nothing}</span
	>`;
}

export interface FieldMessageOptions {
	/** The text to show — callers resolve `error || helpText` themselves
	 * (the "error replaces helpText" rule) and pass the winner here. */
	message: string;
	isError: boolean;
	/** Set when the control needs `aria-describedby` wired to this span. */
	id?: string;
	/** Set when a `cos-help-dock` ancestor is showing this field's help
	 * instead (fleet/helpdock) — clips the text out of view with the same
	 * `.visually-hidden` technique as a hidden label, rather than removing
	 * it, so `aria-describedby` still resolves it for assistive tech.
	 * Callers only ever set this for help copy, never for an error: an
	 * error stays visible inline regardless of the dock. */
	visuallyHidden?: boolean;
}

/**
 * The help/error text row beneath a field's control. Renders `nothing`
 * when `message` is empty.
 */
export function renderFieldMessage(opts: FieldMessageOptions): TemplateResult | typeof nothing {
	if (!opts.message) return nothing;
	const classes = [opts.isError ? 'msg error' : 'msg help', opts.visuallyHidden ? 'visually-hidden' : '']
		.filter(Boolean)
		.join(' ');
	return html`<span id=${opts.id ?? nothing} class=${classes}>${opts.message}</span>`;
}

/**
 * Resolves the message to show and its tone in one place — the "error
 * replaces helpText" rule every field primitive follows.
 */
export function fieldMessage(helpText: string, error: string): { message: string; isError: boolean } {
	return error ? { message: error, isError: true } : { message: helpText, isError: false };
}

/**
 * The class a field primitive's own "control box" — the bordered element
 * that visually IS the control (a trigger button, a chip/option row, a
 * checkbox box, a toggle track, a segmented group, a radio dot) — adds to
 * ITSELF, alongside its own class, when `error` is set. Pair with
 * `controlErrorStyles` below: that one rule owns the colour, so no
 * primitive retypes `border-color: var(--cos-status-danger-text)` on its
 * own — the same reasoning as `fieldMessage` centralizing the
 * error-replaces-helpText resolution instead of every render() reimplementing
 * the ternary.
 *
 * cos-input/cos-textarea/cos-date-picker predate this helper and already
 * mark their own control box on `error` (via `:has()`, `[aria-invalid]`, and
 * a local `.trigger.error` respectively) — correct, untouched, and not
 * migrated onto this, since the goal is one mechanism for the primitives
 * that don't have one, not a mandatory rewrite of the ones that already do.
 */
export function controlErrorClass(hasError: boolean): string {
	return hasError ? ' field-error-box' : '';
}

/**
 * The one rule for `controlErrorClass`'s marker class. A primitive imports
 * this (on its own, or via `fieldAnatomyStyles` below) and never writes its
 * own `border-color: var(--cos-status-danger-text)` rule for the control box.
 *
 * The selector is DELIBERATELY doubled. A control box sets its resting
 * border with the `border:` shorthand — `.trigger { border: 1px solid
 * var(--cos-border) }` — which is specificity (0,1,0), identical to a
 * single `.field-error-box`. Most primitives interpolate the anatomy
 * styles at the TOP of their `static styles`, so their own rule comes
 * later and, at equal specificity, silently wins: the class is applied,
 * the rule is in the stylesheet, and the border still renders grey.
 * Doubling the class lifts this to (0,2,0) so it wins wherever the
 * primitive chooses to place the import, instead of making every current
 * and future primitive remember to append it last.
 *
 * Note this is invisible to a test that asserts the class is present or
 * that the rule exists — only a COMPUTED border colour catches it, which
 * is why the tests below read getComputedStyle rather than the class list.
 */
export const controlErrorStyles = css`
	.field-error-box.field-error-box {
		border-color: var(--cos-status-danger-text);
	}
`;

/**
 * The standard clip-rect "visually hidden but still in the a11y tree"
 * technique (`display:none` would remove it from the accessible name/
 * description computation too). Exported separately from
 * `fieldAnatomyStyles` so a primitive with its own distinct label/message
 * idiom (`cos-checkbox`) can pull in just this rule rather than the whole
 * anatomy stylesheet.
 */
export const visuallyHiddenStyles = css`
	.visually-hidden {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}
`;

export const fieldAnatomyStyles = css`
	/* Geometry here follows docs/prototype/communico-roster.html — the only
	   prototype in the repo that designs form fields (.form-label: 13px/600,
	   6px above its control; .form-input: 9px 12px, 1.5px border, 8px radius).
	   The Quiet v3 handoff specifies no form controls at all, which is why
	   these had been carrying cos-select's trigger geometry by default rather
	   than a designed one. Change them here, not per primitive. */
	.label-text {
		font-size: var(--cos-font-size-sm);
		font-weight: 600;
		color: var(--cos-text-primary);
	}
	${visuallyHiddenStyles}
	.req {
		color: var(--cos-status-danger-text);
	}
	.msg {
		font-size: var(--cos-font-size-xs);
	}
	.msg.help {
		color: var(--cos-text-secondary);
	}
	.msg.error {
		color: var(--cos-status-danger-text);
	}
	${controlErrorStyles}
`;
